/**
 * Centralized API constants to eliminate magic strings and hardcoded values.
 * Follows the project's "100% Dynamic" and "No Magic Values" mandates.
 */

export const API_ERROR_CODES = {
  // Authentication & Authorization
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  FORBIDDEN_ERROR: 'FORBIDDEN_ERROR', // Legacy alias from errorUtils
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  SESSION_EXPIRED: 'SESSION_EXPIRED', // Used in apiClient
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  INVALID_CURRENT_PASSWORD: 'INVALID_CURRENT_PASSWORD',
  PASSWORD_REUSED: 'PASSWORD_REUSED',
  CAPTCHA_FAILED: 'CAPTCHA_FAILED',
  INVALID_2FA_CODE: 'INVALID_2FA_CODE',
  TWO_FA_NOT_CONFIGURED: '2FA_NOT_CONFIGURED',
  TWO_FA_ALREADY_ENABLED: '2FA_ALREADY_ENABLED',

  // Validation
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  VALIDATION_CONFLICT: 'VALIDATION_CONFLICT',
  MISSING_REQUIRED_FIELD: 'MISSING_REQUIRED_FIELD',
  MISSING_FIELDS: 'MISSING_FIELDS', // Legacy alias
  USER_EMAIL_CONFLICT: 'USER_EMAIL_CONFLICT',
  USER_USERNAME_CONFLICT: 'USER_USERNAME_CONFLICT',
  USER_UNIVERSITY_ID_CONFLICT: 'USER_UNIVERSITY_ID_CONFLICT',
  PASSWORD_TOO_SHORT: 'PASSWORD_TOO_SHORT',

  // Resources
  NOT_FOUND: 'NOT_FOUND',
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
  DUPLICATE_ENTRY: 'DUPLICATE_ENTRY',

  // Server
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',

  // Rate Limiting
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',

  // Payment
  PAYMENT_FAILED: 'PAYMENT_FAILED',
  INSUFFICIENT_FUNDS: 'INSUFFICIENT_FUNDS',

  // Unknown
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

/**
 * Backend Transient Error Codes
 *
 * These are the ACTUAL error code strings emitted by the backend's globalErrorHandler
 * for transient infrastructure failures. Used by frontend retry logic to classify
 * errors as retryable without scattering magic strings across hooks.
 *
 * @see apps/backend/src/constants/error-codes.ts
 */
export const TRANSIENT_ERROR_CODES = {
  /** Backend Admission Control / Circuit Breaker rejection */
  SERVICE_UNAVAILABLE: 'E50301',
  /** Upstream gateway timeout (payment processor, external API) */
  GATEWAY_TIMEOUT: 'E50401',
} as const;

/**
 * Backend Session Fingerprint Error Codes
 *
 * Emitted by the Adaptive 3-Tier Fingerprint Engine in authMiddleware.
 * The frontend uses these to choose between a silent refresh attempt (device transition)
 * and a forced logout (confirmed hijack/hard mismatch).
 *
 * @see apps/backend/src/constants/error-codes.ts
 * @see apps/backend/src/middlewares/authMiddleware.ts
 */
export const BACKEND_SESSION_ERROR_CODES = {
  /**
   * E40115 — Tier 3 device transition detected (User-Agent changed).
   * The backend cannot confirm it is a hijack vs. a legitimate cross-device handoff.
   * Frontend should attempt a silent token refresh. If refresh succeeds, the new
   * access token is bound to the new device's fingerprint — seamless handoff.
   * If refresh fails (expired/revoked), fall through to SESSION_EXPIRED (force logout).
   */
  DEVICE_TRANSITION: 'E40115',
  /**
   * E40114 — Hard session binding mismatch (structural divergence, drift threshold exceeded).
   * High-confidence signal of session abuse. Force immediate logout — do NOT attempt refresh.
   */
  BOUND_MISMATCH: 'E40114',
} as const;

/**
 * Centralized Authentication Routes
 * 
 * DESIGN RATIONALE:
 * - These routes define the "Authentication Domain" of the application.
 * - By using a dynamic getter, we ensure that the actual paths (e.g., /login) 
 *   can be updated in .env without rebuilding the code.
 * - This provides "Maximum Performance" as the value is resolved via the cached ConfigurationService.
 */


/**
 * IMPORTANT: API_CONFIG is now dynamic and fetched from backend.
 * These getters proxy to ConfigurationService which loads config at runtime.
 * Do NOT import this directly in module scope - use functional calls only.
 *
 * Migration Guide:
 * - Old: const timeout = API_CONFIG.TIMEOUT_MS;
 * - New: const timeout = getApiConfig().TIMEOUT_MS;
 */
import ConfigurationService from '../services/ConfigurationService';

export const getApiConfig = () => ({
  TIMEOUT_MS: ConfigurationService.getApiTimeoutMs(),
  MAX_RETRIES: ConfigurationService.getApiMaxRetries(),
  DATE_FORMAT: ConfigurationService.getDateFormat(),
});

/**
 * HTTP Status codes whitelisted for retry policy (transient server failures)
 */
export const RETRYABLE_HTTP_STATUS_CODES = [502, 503, 504] as const;

/**
 * HTTP Status codes blacklisted from retry policy (client errors/validation errors)
 */
export const NON_RETRYABLE_HTTP_STATUS_CODES = [400, 401, 403, 404, 409, 422] as const;

